import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShieldCheck, Check, ArrowRight, Loader2, Mail, Lock } from "lucide-react";
import { toast } from "sonner";
import { BRAND, HERO_IMAGES } from "../../lib/mockData";
import { api, auth } from "../../lib/api";

export default function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setLoading(true);
    try {
      const r = await api.adminLogin(email, password);
      auth.set(r.token, r.user);
      toast.success(`Welcome, ${r.user.name || "Admin"}`);
      navigate("/admin");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const points = [
    "Monitor leads & appointments platform-wide",
    "Manually assign customer briefs to attorneys",
    "Approve profile changes before they go live",
  ];

  return (
    <div className="min-h-screen grid lg:grid-cols-2" data-testid="admin-login-page">
      <div className="hidden lg:flex relative surface-deep text-white flex-col justify-between p-12 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] blob bg-[#7CC242]" />
        <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] blob bg-[#0E6E5E]" />

        <Link to="/" className="relative flex items-center gap-3 w-fit">
          <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-[#0E6E5E] to-[#0A3B36] flex items-center justify-center ring-1 ring-white/10">
            <span className="font-display font-black text-2xl text-[#7CC242]">U</span>
          </div>
          <div>
            <div className="font-display font-extrabold text-2xl">{BRAND.name}</div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-[#7CC242] font-semibold mt-0.5">Admin Console</div>
          </div>
        </Link>

        <div className="relative">
          <img src={HERO_IMAGES.office} alt="" className="rounded-3xl aspect-[16/10] object-cover w-full mb-8 opacity-90" />
          <div className="chip chip-gold inline-flex mb-4"><ShieldCheck size={13}/> Admin Portal</div>
          <h2 className="font-display font-extrabold text-4xl leading-tight mb-6">Platform control<br/>at your fingertips.</h2>
          <div className="space-y-3">
            {points.map((p) => (
              <div key={p} className="flex items-center gap-3 text-white/80">
                <div className="w-6 h-6 rounded-full bg-[#7CC242] flex items-center justify-center flex-shrink-0">
                  <Check size={13} className="text-[#0A2520]"/>
                </div>
                <span>{p}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="relative text-xs text-white/40">© 2025 Udhyak Technologies Pvt Ltd · Restricted access</div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12 surface-cream">
        <form onSubmit={submit} className="w-full max-w-md">
          <div className="chip mb-4 inline-flex"><ShieldCheck size={13}/> Admin Login</div>
          <h1 className="font-display font-extrabold text-4xl text-[#0A2520] mb-2">Sign in to console</h1>
          <p className="text-[#5A6B66] mb-8">Restricted access for Udhyak platform administrators only.</p>

          <label className="block font-display text-sm font-semibold text-[#0A2520] mb-2">Work email</label>
          <div className="relative mb-5">
            <Mail size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#5A6B66]"/>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@udhyak.in"
              className="input pl-11"
              data-testid="admin-email-input"
              autoComplete="email"
            />
          </div>

          <label className="block font-display text-sm font-semibold text-[#0A2520] mb-2">Password</label>
          <div className="relative mb-6">
            <Lock size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#5A6B66]"/>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="input pl-11"
              data-testid="admin-password-input"
              autoComplete="current-password"
            />
          </div>

          <button
            type="submit"
            disabled={loading || !email || !password}
            className="btn-primary w-full justify-center disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="admin-login-btn"
          >
            {loading ? <Loader2 size={16} className="animate-spin"/> : <>Sign in <ArrowRight size={16}/></>}
          </button>

          <div className="flex items-center gap-2 mt-6 text-xs text-[#5A6B66]">
            <ShieldCheck size={13} className="text-[#0E6E5E]"/>
            Sessions expire after 7 days. All activity is logged.
          </div>
        </form>
      </div>
    </div>
  );
}
